import { memo, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { AnimatePresence, motion } from 'motion/react';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { NakedButton, Text, Truncated } from '@frontend/design-system';
import { CalendarGridStore, useCalendarGridStore } from '../store/calendar-grid.store';
import { AllDayCalendarEvent } from '../types';

const BAR_COLLAPSE_HEIGHT = 24;

interface AllDayEventsBarProps {
  onAllDayEventClick?: (event: AllDayCalendarEvent) => void;
}

const AllDayEventsBarComponent = ({ onAllDayEventClick }: AllDayEventsBarProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const { groupIds, groupsById, allDayEvents, hasAllDayEvents } = useCalendarGridStore([
    'groupIds',
    'groupsById',
    'allDayEvents',
    'hasAllDayEvents',
  ]);

  const gridTemplateColumns = useMemo(() => {
    return groupIds.reduce((acc, groupId) => {
      const columnCount = groupsById[groupId]?.columnIds.length ?? 0;
      if (columnCount === 0) return acc;
      return `${acc} repeat(${columnCount}, minmax(var(--column-min-width), 1fr)) var(--group-separator-width)`;
    }, 'var(--time-column-width) ');
  }, [groupIds, groupsById]);

  if (!hasAllDayEvents) return null;

  return (
    <AnimatePresence>
      <motion.section key='all-day-bar' css={containerStyle} onClick={() => setIsExpanded((prev) => !prev)}>
        <div css={innerGridStyle} style={{ gridTemplateColumns }}>
          <motion.div
            key={isExpanded ? 'toggle-expanded' : 'toggle-collapsed'}
            css={toggleCellStyle}
            initial={{ opacity: 0.5 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0.5 }}
            transition={{ duration: 0.3, ease: 'linear' }}
          >
            <NakedButton
              css={toggleButtonStyle}
              aria-label={isExpanded ? 'Collapse all-day events' : 'Expand all-day events'}
            >
              <Icon name={isExpanded ? 'collapse-small' : 'expand-small'} />
            </NakedButton>
          </motion.div>

          <motion.div
            css={clippedContentStyle}
            initial={{ height: 0 }}
            animate={{ height: isExpanded ? 'max-content' : BAR_COLLAPSE_HEIGHT }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
          >
            <GroupSections
              groupIds={groupIds}
              groupsById={groupsById}
              allDayEvents={allDayEvents}
              isExpanded={isExpanded}
              onAllDayEventClick={onAllDayEventClick}
            />
          </motion.div>
        </div>
      </motion.section>
    </AnimatePresence>
  );
};

export const AllDayEventsBar = memo(AllDayEventsBarComponent);

// ─── Group Sections ───────────────────────────────────────────────────────────

interface GroupSectionsProps {
  groupIds: string[];
  groupsById: CalendarGridStore['groupsById'];
  allDayEvents: AllDayCalendarEvent[];
  isExpanded: boolean;
  onAllDayEventClick?: (event: AllDayCalendarEvent) => void;
}

const GroupSections = ({ groupIds, groupsById, allDayEvents, isExpanded, onAllDayEventClick }: GroupSectionsProps) => {
  return (
    <>
      {groupIds.map((groupId, groupIndex) => {
        const group = groupsById[groupId];
        if (!group || group.columnIds.length === 0) return null;

        // mirrors the startColumn formula used in column-group-sections/index.tsx
        const startColumn = groupIndex + group.groupOffset + 2;
        const columnCount = group.columnIds.length;
        const groupLevelEvents = allDayEvents.filter((e) => e.groupId === groupId && e.scope === 'group');

        return (
          <div key={groupId} css={groupSectionStyle} style={{ gridColumn: `${startColumn} / span ${columnCount}` }}>
            <AnimatePresence mode='popLayout'>
              {isExpanded ? (
                <motion.div
                  key='expanded'
                  css={expandedGridStyle}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2, ease: 'easeInOut' }}
                >
                  {/* Group-level events span the full group width, one per row */}
                  {groupLevelEvents.map((event, rowIndex) => (
                    <EventCard
                      key={event.id}
                      event={event}
                      onAllDayEventClick={onAllDayEventClick}
                      gridColumn='1 / -1'
                      gridRow={rowIndex + 1}
                    />
                  ))}
                  {/* Column-level events live in their own column track, stacking into new rows when a column has more than one */}
                  {group.columnIds.flatMap((columnId, columnIndex) => {
                    const columnEvents = allDayEvents.filter((e) => e.scope === 'column' && e.columnId === columnId);
                    return columnEvents.map((event, stackIndex) => (
                      <EventCard
                        key={event.id}
                        event={event}
                        onAllDayEventClick={onAllDayEventClick}
                        gridColumn={`${columnIndex + 1} / span 1`}
                        gridRow={groupLevelEvents.length + stackIndex + 1}
                      />
                    ));
                  })}
                </motion.div>
              ) : (
                <motion.div
                  key='collapsed'
                  css={collapsedGridStyle}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  transition={{ duration: 0.25, ease: 'easeInOut' }}
                >
                  {group.columnIds.map((columnId, columnIndex) => {
                    const columnEventCount = allDayEvents.filter(
                      (e) => e.scope === 'column' && e.columnId === columnId
                    ).length;
                    const totalCount = groupLevelEvents.length + columnEventCount;
                    return (
                      <CollapsedColumnCount
                        key={columnId}
                        count={totalCount}
                        gridColumn={`${columnIndex + 1} / span 1`}
                      />
                    );
                  })}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </>
  );
};

// ─── Collapsed count cell ─────────────────────────────────────────────────────

const CollapsedColumnCount = ({ count, gridColumn }: { count: number; gridColumn: string }) => {
  const { t } = useTranslation('calendar-grid');
  if (count === 0) return null;
  return (
    <div css={countCellStyle} style={{ gridColumn }}>
      <Text size='small' weight='bold'>
        {t('{{count}} event', { count })}
      </Text>
    </div>
  );
};

// ─── Event card ───────────────────────────────────────────────────────────────

interface EventCardProps {
  event: AllDayCalendarEvent;
  onAllDayEventClick?: (event: AllDayCalendarEvent) => void;
  gridColumn: string;
  gridRow: number;
}

const EventCard = ({ event, onAllDayEventClick, gridColumn, gridRow }: EventCardProps) => {
  const iconName = event.icon ?? (event.scope === 'group' ? 'location-small' : 'remove-small');

  return (
    <motion.button
      type='button'
      css={eventCardStyle}
      style={{ gridColumn, gridRow }}
      initial={{ opacity: 0, y: -4 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -4 }}
      transition={{ duration: 0.25, ease: 'easeInOut' }}
      whileHover={{ scale: 1.002, boxShadow: theme.shadows.floating }}
      onClick={(e) => {
        e.stopPropagation(); // prevent collapsing the bar on card click
        onAllDayEventClick?.(event);
      }}
    >
      <Icon name={iconName} css={{ flexShrink: 0 }} />
      <Truncated as={Text} size='small' weight='bold'>
        {event.name}
      </Truncated>
    </motion.button>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const containerStyle = css({
  gridRow: 2,
  gridColumn: '1 / -1',
  borderTop: `1px solid ${theme.colors.neutral20}`,
  backgroundColor: theme.colors.neutral5,
  cursor: 'pointer',
  boxShadow: theme.shadows.light,
});

const innerGridStyle = css({
  display: 'grid', // matches the main calendar column layout via inline gridTemplateColumns
});

const clippedContentStyle = css({
  display: 'grid',
  gridTemplateColumns: 'subgrid',
  gridColumn: '1 / -1',
  gridRow: 1,
  overflow: 'hidden',
});

const toggleCellStyle = css({
  gridColumn: 1,
  gridRow: 1,
  position: 'sticky',
  left: 0,
  backgroundColor: theme.colors.neutral5,
  zIndex: 1,
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'flex-end',
});

const toggleButtonStyle = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  width: '100%',
  padding: theme.spacing(0.5, 1),
});

const groupSectionStyle = css({
  display: 'grid',
  gridTemplateColumns: 'subgrid', // aligns group/column events to the same column tracks as the ColumnHeader pills
  position: 'relative', // required for popLayout — scopes the absolutely-positioned exiting element
  overflow: 'hidden',
  minWidth: 0,
});

const expandedGridStyle = css({
  display: 'grid',
  gridTemplateColumns: 'subgrid',
  gridColumn: '1 / -1',
  gap: theme.spacing(0.5),
  padding: theme.spacing(0.5, 0.25),
});

const collapsedGridStyle = css({
  display: 'grid',
  gridTemplateColumns: 'subgrid',
  gridColumn: '1 / -1',
  height: '100%',
});

const countCellStyle = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(0, 0.5),
  minWidth: 0,
});

const eventCardStyle = css({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  borderLeft: `4px solid ${theme.colors.neutral60}`,
  borderTop: 'none',
  borderRight: 'none',
  borderBottom: 'none',
  backgroundColor: theme.colors.neutral20,
  borderRadius: 2,
  padding: theme.spacing(0.25, 1),
  width: '100%',
  boxShadow: theme.shadows.light,
  cursor: 'pointer',
  textAlign: 'left',
});
